import type { SentenceToken } from "./tokens"
import type {
  SelectCorrectChoiceInput,
  SelectCorrectDb,
  SelectCorrectDbSentence,
  SelectCorrectRuleMatch,
  SelectCorrectRuleStrategy,
  SelectCorrectSentence,
} from "./types"

import { logger } from "../modules/logger/logger"
import { normalizeKey, splitSentence, splitSentenceWithOffsets } from "./tokens"

const DEFINITARTIKEL_FORMS = ["der", "den", "dem"]
const DEFINITARTIKEL_LABEL = "der?"
const RICHTUNGSPRAEPOSITION_LABEL = "in?"
/** Canonical order of the direction DB — also the order of the two buttons. */
const RICHTUNGSPRAEPOSITION_FORMS = ["an", "auf", "in", "um", "zu"]
const LOKALE_PRAEPOSITION_LABEL = "aus?"
/** Canonical order of the local DB — also the order of the two buttons. */
const LOKALE_PRAEPOSITION_FORMS = ["aus", "bei", "nach"]
/** Canonical order of the causal DB — also the order of the two buttons. */
const KAUSALE_PRAEPOSITION_FORMS = ["aus", "vor"]
/**
 * Rule 11 blanks the whole causal preposition and either member can be the
 * answer, so a member-based label (`aus?`) would name the answer in 9 of 24
 * questions. The slot stays neutral, like rule 8.
 */
const KAUSALE_PRAEPOSITION_LABEL = "___?"
/** Ending and morpheme blanks (rules 5–7, 9) leave no full form pending. */
const ENDING_LABEL = "-?"
/**
 * Rule 8 blanks a whole adverb and its answer is `options[0]`, so the slot must
 * not hint at any member of the contrast set.
 */
const ORTSADVERB_LABEL = "___?"
/**
 * Rule 10 blanks a word-initial prefix (`[___]geben`), so a trailing dash would
 * read as an ending marker on the wrong side. The label stays neutral and its
 * slot width is pinned to the widest prefix.
 */
const VERBPRAEFIX_LABEL = "___?"
/**
 * `zu` is dative-only and `um` accusative-only in the direction DB (607/607 and
 * 94/94), so the distractor always contrasts the case instead of offering a
 * second form that also fits the sentence.
 */
const OPPOSITE_CASE_PREPOSITION: Record<string, string> = {
  Acc: "zu",
  Dat: "um",
}

/**
 * Rule 1 — Definitartikel Maskulin Singular.
 * `dem` is syncretic for masculine/neuter, so ambiguous-gender matches would
 * teach the wrong gender and are dropped (the DB's own `strict` split).
 */
export const DEFINITARTIKEL_STRATEGY: SelectCorrectRuleStrategy = {
  acceptMatch: match=>
    match.genderSource !== "ambiguous" && Boolean(match.article),
  buildChoices: buildDefinitartikelChoices,
  label: ()=> DEFINITARTIKEL_LABEL,
  markerKey: match=> match.article ?? "",
}

/**
 * Rule 2 — Richtungspräposition.
 * The preposition is blanked; the visible determiner (`dem` / `den`) carries
 * the case cue, so a single opposite-case preposition is the honest distractor.
 */
export const RICHTUNGSPRAEPOSITION_STRATEGY: SelectCorrectRuleStrategy = {
  acceptMatch: match=>
    Boolean(match.preposition) &&
    (match.caseName === "Acc" || match.caseName === "Dat"),
  buildChoices: buildPrepositionChoices,
  label: ()=> RICHTUNGSPRAEPOSITION_LABEL,
  markerKey: match=> match.preposition ?? "",
}

/**
 * Rule 3 — Akkusativ/Dativ im ersten Satzteil.
 * The marker is the DB's own hidden word (`hidden.form`); its paradigm
 * (`match.options`) becomes the buttons, so the item asks for the form that
 * fits the visible nominal chunk — not for the case name. Rows without a
 * paradigm (bare nouns, proper names) produce no question.
 */
export const AKKUSATIV_DATIV_STRATEGY: SelectCorrectRuleStrategy = {
  acceptMatch: match=> Array.isArray(match.options) && match.options.length > 0,
  buildChoices: buildParadigmChoices,
  label: match=> `${match.options?.[0] ?? ""}?`,
  markerKey: match=> match.hidden?.form ?? phraseKeys(match.phrase)[0] ?? "",
}

/**
 * Rule 4 — Lokale Präposition (`aus` / `bei` / `nach`).
 * The case cannot choose here (`aus Berlin`, `nach Stuttgart` are article-less),
 * so the sentence has to carry the sense: place/origin for `aus`/`nach`, person,
 * name or event for `bei`. The DB already filtered by that gate; the UI blanks
 * the preposition and asks which of the members fits.
 */
export const LOKALE_PRAEPOSITION_STRATEGY: SelectCorrectRuleStrategy = {
  acceptMatch: match=>
    LOKALE_PRAEPOSITION_FORMS.includes(
      (match.preposition ?? "").toLowerCase(),
    ),
  buildChoices: buildLocalPrepositionChoices,
  label: ()=> LOKALE_PRAEPOSITION_LABEL,
  markerKey: match=> match.preposition ?? "",
}

/**
 * Rule 5 — Kasus in Redewendungen.
 * The idiom, not the sentence, fixes the form: the blank may be the article
 * (`auf die Palme`), article + adjective (`an die große Glocke`) or only the
 * adjective ending (`hinter schwedisch[___]`). Those are char ranges in the DB's
 * `hidden`, and its `options` are the generated paradigm — so this rule never
 * derives a form, it only renders one.
 */
export const REDEWENDUNG_STRATEGY: SelectCorrectRuleStrategy = {
  acceptMatch: hasAnswerInOptions,
  buildChoices: buildParadigmChoices,
  label: redewendungLabel,
  markerKey: redewendungMarkerKey,
}

/**
 * Rule 6 — Wechselpräposition: Endung des Artikels.
 * The DB already picked the phrase after a two-way preposition, so the question
 * only asks which ending the determiner takes: the blank is the stored char
 * range and `options` is that determiner's paradigm.
 */
export const WECHSEL_ENDUNG_STRATEGY: SelectCorrectRuleStrategy = {
  acceptMatch: hasAnswerInOptions,
  buildChoices: buildParadigmChoices,
  label: ()=> ENDING_LABEL,
  markerKey: match=> match.hidden?.form ?? "",
}

/**
 * Rule 7 — Richtungsadverb hin/her.
 * The blank is the `hin`/`her` morpheme inside the adverb (`[__]ein`, `wo[__]`,
 * `hier[__]`) or the whole bare word, and `options` is that fixed pair — so the
 * sentence keeps the visible rest and only the direction morpheme is asked for.
 */
export const RICHTUNGSADVERB_STRATEGY: SelectCorrectRuleStrategy = {
  acceptMatch: hasAnswerInOptions,
  buildChoices: buildParadigmChoices,
  label: ()=> ENDING_LABEL,
  markerKey: match=> match.hidden?.form ?? "",
}

/**
 * Rule 8 — Ortsadverb.
 * The blank is the whole adverb and `options` is its contrast set (`oben ·
 * unten`, `da · hier · dort`). The DB stores the answer first, which would pin
 * the correct button to the left, so the pair's side is decided per build.
 */
export const ORTSADVERB_STRATEGY: SelectCorrectRuleStrategy = {
  acceptMatch: hasAnswerInOptions,
  buildChoices: buildOrtsadverbChoices,
  label: ()=> ORTSADVERB_LABEL,
  markerKey: match=> match.hidden?.form ?? "",
}

/**
 * Rule 9 — Determinativendung.
 * The blank is only the determiner's ending (`jed[er]`, `sämtlich[e]`,
 * `diejenig[en]`) and `options` is that lemma's ending set (weak lemmas offer
 * `e · en`), so the visible stem carries the question. No ending-mode filter:
 * the rule ships no `mode`, so every ending of the lemma stays available.
 */
export const DETERMINER_ENDING_STRATEGY: SelectCorrectRuleStrategy = {
  acceptMatch: hasAnswerInOptions,
  buildChoices: buildParadigmChoices,
  label: ()=> ENDING_LABEL,
  markerKey: match=> match.hidden?.form ?? "",
}

/**
 * Rule 10 — Verbpräfix.
 * The blank is word-initial and the stem stays visible (`[___]geben`,
 * `[___]zudrehen`); every row offers the same eight prefixes, and the rule
 * ships no `mode`, so the ending filter must stay a pass-through. The slot is
 * sized to the longest prefix so its width never reveals the answer's length.
 */
export const VERBPRAEFIX_STRATEGY: SelectCorrectRuleStrategy = {
  acceptMatch: hasAnswerInOptions,
  buildChoices: buildParadigmChoices,
  label: ()=> VERBPRAEFIX_LABEL,
  markerKey: match=> match.hidden?.form ?? "",
  slotWidth: longestFormLength,
}

/**
 * Rule 11 — Kausalpräposition `vor` / `aus`.
 * The blank is the whole preposition and `options` is the fixed pair, so the
 * buttons come from the member set rather than the stored array. The sense of
 * the governor (`vor Angst`, `aus Höflichkeit`) already gated the DB; the UI
 * only asks which of the two members fits, with the other one as distractor.
 */
export const KAUSALE_PRAEPOSITION_STRATEGY: SelectCorrectRuleStrategy = {
  acceptMatch: match=>
    KAUSALE_PRAEPOSITION_FORMS.includes(
      (match.preposition ?? "").toLowerCase(),
    ) && hasAnswerInOptions(match),
  buildChoices: buildKausalePrepositionChoices,
  label: ()=> KAUSALE_PRAEPOSITION_LABEL,
  markerKey: match=> match.preposition ?? "",
}

export interface BuildSentencesInput {
  database: SelectCorrectDb
  pickRandom: (length: number)=> number
  strategy: SelectCorrectRuleStrategy
}

interface Blank {
  match: SelectCorrectRuleMatch
  /** Visible text of the same token in front of the blank (rule 5 endings). */
  prefix: string
  /** Visible text of the same token behind the blank. */
  suffix: string
  /** First covered token — the question's position in the sentence. */
  tokenIndex: number
  /** Every token the blank covers: 1 for an article, 2 for article + adjective. */
  tokenIndexes: number[]
  /** The rendered answer, sliced out of the sentence text. */
  value: string
}

interface IndexedToken extends SentenceToken {
  index: number
}

/**
 * Parses the raw DB into renderable sentences. A match survives only when its
 * phrase still has a matching span and its marker token has not been blanked by
 * another match yet; sentences left without any question are dropped.
 */
export function buildSentences(
  input: BuildSentencesInput,
): SelectCorrectSentence[] {
  const attestedAnswers = collectAttestedAnswers(input.database)
  const sentences = input.database.sentences.flatMap(sentence=> {
    const built = buildSentence({
      attestedAnswers,
      pickRandom: input.pickRandom,
      sentence,
      strategy: input.strategy,
    })
    return built ? [built] : []
  })
  reportDroppedMatches({ database: input.database, sentences })
  return sentences
}

/**
 * The answer forms the rule itself asks for. Rule 5 stores a whole declension
 * paradigm as `options`, including the genitive `es` behind `des` — an ending
 * the rule never answers with. Collected once per DB so endings questions can
 * keep that ending out of their distractors.
 */
function collectAttestedAnswers(database: SelectCorrectDb): string[] {
  const keys = database.sentences.flatMap(sentence=> {
    return sentence.matches.flatMap(match=> {
      if (!isEndingMode(match)) return []
      const answerKey = normalizeKey(match.hidden?.form ?? "")
      if (!answerKey) return []
      const isAnswerable = (match.options ?? []).some(
        form=> normalizeKey(form) === answerKey,
      )
      return isAnswerable ? [answerKey] : []
    })
  })
  return [...new Set(keys)]
}

/**
 * Not every match becomes a question: rule 1 drops ambiguous genders, rule 3
 * drops markers without a paradigm, and the tokenizer cannot locate a handful
 * of hyphen-split / footnote phrases. Surface the count instead of losing rows
 * silently.
 */
function reportDroppedMatches(input: {
  database: SelectCorrectDb
  sentences: SelectCorrectSentence[]
}): void {
  const matchCount = input.database.sentences.reduce(
    (total, sentence)=> total + sentence.matches.length,
    0,
  )
  const questionCount = input.sentences.reduce(
    (total, sentence)=> total + sentence.questions.length,
    0,
  )
  const dropped = matchCount - questionCount
  if (dropped <= 0) return

  logger({
    input: `select-correct: ${dropped} match(es) skipped as unanswerable (${input.database.ruleId})`,
    level: "warning",
  })
}

function buildSentence(input: {
  attestedAnswers: string[]
  pickRandom: (length: number)=> number
  sentence: SelectCorrectDbSentence
  strategy: SelectCorrectRuleStrategy
}): null | SelectCorrectSentence {
  const tokens = splitSentenceWithOffsets(input.sentence.text)
  const blanks = locateBlanks({
    matches: input.sentence.matches,
    strategy: input.strategy,
    text: input.sentence.text,
    tokens,
  })
  if (blanks.length === 0) return null

  const questionsByTokenIndex = buildQuestions({
    attestedAnswers: input.attestedAnswers,
    blanks,
    pickRandom: input.pickRandom,
    strategy: input.strategy,
  })

  return {
    questions: blanks.flatMap(blank=> {
      const question = questionsByTokenIndex.get(blank.tokenIndex)
      return question ? [question] : []
    }),
    words: buildWords({ blanks, questionsByTokenIndex, tokens }),
  }
}

function buildQuestions(input: {
  attestedAnswers: string[]
  blanks: Blank[]
  pickRandom: (length: number)=> number
  strategy: SelectCorrectRuleStrategy
}): Map<number, QuestionWord> {
  return new Map(
    input.blanks.map(blank=> [
      blank.tokenIndex,
      createQuestion({
        attestedAnswers: input.attestedAnswers,
        blank,
        pickRandom: input.pickRandom,
        strategy: input.strategy,
      }),
    ]),
  )
}

/**
 * One question word per blank, at the first token it covers; the rest of a
 * multi-token blank is dropped so the sentence does not show the answer twice.
 */
function buildWords(input: {
  blanks: Blank[]
  questionsByTokenIndex: Map<number, QuestionWord>
  tokens: SentenceToken[]
}): Word[] {
  const coveredTokenIndexes = new Set(
    input.blanks.flatMap(blank=> blank.tokenIndexes),
  )

  return input.tokens.flatMap((token, index)=> {
    const question = input.questionsByTokenIndex.get(index)
    if (question) return [question]
    return coveredTokenIndexes.has(index) ? [] : [{ value: token.value }]
  })
}

function locateBlanks(input: {
  matches: SelectCorrectRuleMatch[]
  strategy: SelectCorrectRuleStrategy
  text: string
  tokens: SentenceToken[]
}): Blank[] {
  const blankedTokenIndexes = new Set<number>()
  const wordTokenIndexes = indexesOfWordTokens(input.tokens)

  return input.matches
    .flatMap(match=> {
      const blank = locateBlank({
        blankedTokenIndexes,
        match,
        strategy: input.strategy,
        text: input.text,
        tokens: input.tokens,
        wordTokenIndexes,
      })
      return blank ? [blank] : []
    })
    .sort((a, b)=> a.tokenIndex - b.tokenIndex)
}

function locateBlank(input: {
  blankedTokenIndexes: Set<number>
  match: SelectCorrectRuleMatch
  strategy: SelectCorrectRuleStrategy
  text: string
  tokens: SentenceToken[]
  wordTokenIndexes: number[]
}): Blank | null {
  if (!input.strategy.acceptMatch(input.match)) return null

  // The DB's char offsets are authoritative: they may cover one token, two, or
  // only the ending inside a word (`d[ie]`, `schwedisch[en]`). The phrase
  // lookup below is the fallback for rows without usable offsets.
  const hidden = input.match.hidden
  if (hidden) {
    const covered = coveringTokens({ hidden, tokens: input.tokens })
    if (covered.length > 0) {
      return blankHiddenRange({
        blankedTokenIndexes: input.blankedTokenIndexes,
        hidden,
        match: input.match,
        text: input.text,
        tokens: input.tokens,
      })
    }
  }

  const markerTokenIndex = locateMarkerTokenIndex(input)
  if (markerTokenIndex === -1) return null

  return blankMarkerToken({
    blankedTokenIndexes: input.blankedTokenIndexes,
    markerTokenIndex: markerTokenIndex,
    match: input.match,
    tokens: input.tokens,
  })
}

/** The phrase's marker token, located the way the pre-`hidden` DBs needed. */
function locateMarkerTokenIndex(input: {
  blankedTokenIndexes: ReadonlySet<number>
  match: SelectCorrectRuleMatch
  strategy: SelectCorrectRuleStrategy
  tokens: SentenceToken[]
  wordTokenIndexes: number[]
}): number {
  const keys = phraseKeys(input.match.phrase)
  const markerKey = normalizeKey(input.strategy.markerKey(input.match))
  const markerOffset = keys.indexOf(markerKey)
  if (markerOffset === -1) return -1

  const start = locatePhrasePosition({
    blankedTokenIndexes: input.blankedTokenIndexes,
    keys,
    markerOffset,
    tokens: input.tokens,
    wordTokenIndexes: input.wordTokenIndexes,
  })
  if (start === -1) return -1

  return input.wordTokenIndexes[start + markerOffset] ?? -1
}

/** Fallback for a match without (usable) char offsets: blank one whole token. */
function blankMarkerToken(input: {
  blankedTokenIndexes: Set<number>
  markerTokenIndex: number
  match: SelectCorrectRuleMatch
  tokens: SentenceToken[]
}): Blank | null {
  if (input.blankedTokenIndexes.has(input.markerTokenIndex)) return null

  const token = input.tokens[input.markerTokenIndex]
  if (!token) return null

  input.blankedTokenIndexes.add(input.markerTokenIndex)
  return {
    match: input.match,
    prefix: "",
    suffix: "",
    tokenIndex: input.markerTokenIndex,
    tokenIndexes: [input.markerTokenIndex],
    value: token.value,
  }
}

/**
 * Blanks the exact chars the DB points at. Everything stays a normal word
 * except the slice itself, so `schwedischen` renders as `schwedisch` + slot.
 */
function blankHiddenRange(input: {
  blankedTokenIndexes: Set<number>
  hidden: NonNullable<SelectCorrectRuleMatch["hidden"]>
  match: SelectCorrectRuleMatch
  text: string
  tokens: SentenceToken[]
}): Blank | null {
  const covered = coveringTokens({ hidden: input.hidden, tokens: input.tokens })
  const first = covered[0]
  const last = covered[covered.length - 1]
  if (!first || !last) return null
  if (covered.some(token=> input.blankedTokenIndexes.has(token.index))) {
    return null
  }

  covered.forEach(token=> input.blankedTokenIndexes.add(token.index))
  return {
    match: input.match,
    prefix: input.text.slice(first.start, input.hidden.start),
    suffix: input.text.slice(input.hidden.end, last.end),
    tokenIndex: first.index,
    tokenIndexes: covered.map(token=> token.index),
    value: input.text.slice(input.hidden.start, input.hidden.end),
  }
}

function coveringTokens(input: {
  hidden: NonNullable<SelectCorrectRuleMatch["hidden"]>
  tokens: SentenceToken[]
}): IndexedToken[] {
  return input.tokens.flatMap((token, index)=>
    token.start < input.hidden.end && input.hidden.start < token.end
      ? [{ index, ...token }]
      : [],
  )
}

/**
 * First span whose keys equal the phrase's keys and whose marker token has not
 * been blanked yet. Punctuation-only tokens are ignored on both sides, so a
 * quoted phrase (`den »Salon«`) still matches, and consuming the marker (not
 * the whole span) lets a repeated phrase map to its next occurrence while
 * nested matches keep their own blank.
 */
function locatePhrasePosition(input: {
  blankedTokenIndexes: ReadonlySet<number>
  keys: string[]
  markerOffset: number
  tokens: SentenceToken[]
  wordTokenIndexes: number[]
}): number {
  if (input.keys.length === 0) return -1

  const lastStart = input.wordTokenIndexes.length - input.keys.length
  for (let start = 0; start <= lastStart; start++) {
    const isMatch = input.keys.every((key, offset)=> {
      const tokenIndex = input.wordTokenIndexes[start + offset]
      if (tokenIndex === undefined) return false
      return normalizeKey(input.tokens[tokenIndex]?.value ?? "") === key
    })
    if (!isMatch) continue

    const markerTokenIndex = input.wordTokenIndexes[start + input.markerOffset]
    if (markerTokenIndex === undefined) continue
    if (input.blankedTokenIndexes.has(markerTokenIndex)) continue
    return start
  }

  return -1
}

function phraseKeys(phrase: string): string[] {
  return splitSentence(phrase)
    .map(normalizeKey)
    .filter(x=> x.length > 0)
}

function indexesOfWordTokens(tokens: SentenceToken[]): number[] {
  return tokens.flatMap((token, index)=>
    normalizeKey(token.value) === "" ? [] : [index],
  )
}

function createQuestion(input: {
  attestedAnswers: string[]
  blank: Blank
  pickRandom: (length: number)=> number
  strategy: SelectCorrectRuleStrategy
}): QuestionWord {
  return {
    correct: null,
    isVisible: false,
    prefix: input.blank.prefix,
    questionGroup: input.strategy.label(input.blank.match),
    selectionChoices: input.strategy.buildChoices({
      answer: input.blank.value,
      attestedAnswers: input.attestedAnswers,
      isCapitalized: startsWithUppercase(input.blank.value),
      match: input.blank.match,
      pickRandom: input.pickRandom,
    }),
    slotWidth: input.strategy.slotWidth?.(input.blank.match),
    suffix: input.blank.suffix,
    value: input.blank.value,
  }
}

/** Ending modes blank only the suffix, so no full form is pending. */
function isEndingMode(match: SelectCorrectRuleMatch): boolean {
  return match.mode?.endsWith("Ending") === true
}

function redewendungLabel(match: SelectCorrectRuleMatch): string {
  if (isEndingMode(match)) return ENDING_LABEL
  return `${match.options?.[0] ?? ""}?`
}

/**
 * A question is only answerable when the DB offered its own form among the
 * options. One imported idiom ships a malformed adjective paradigm
 * (`dem bösen` against `dem böseen`), and a single bad row must not take the
 * whole rule down — it is dropped and counted instead. Rules 5 and 6 share the
 * gate; the DBs assert it in their own stats.
 */
function hasAnswerInOptions(match: SelectCorrectRuleMatch): boolean {
  const forms = match.options ?? []
  const answer = match.hidden?.form ?? ""
  if (forms.length === 0 || answer === "") return false
  const answerKey = normalizeKey(answer)
  return forms.some(form=> normalizeKey(form) === answerKey)
}

/**
 * Rule 10's rows all offer the same prefix set, so the widest option is the
 * slot width every question of that rule shares — a per-answer width would
 * reveal how long the missing prefix is.
 */
function longestFormLength(match: SelectCorrectRuleMatch): number {
  return (match.options ?? []).reduce(
    (longest, form)=> Math.max(longest, form.length),
    0,
  )
}

/**
 * The phrase's marker token: the first word of the blank for article and phrase
 * modes, the adjective itself for an ending blank (whose form is just `-en`).
 */
function redewendungMarkerKey(match: SelectCorrectRuleMatch): string {
  const keySource = isEndingMode(match) ? match.phrase : (match.hidden?.form ?? "")
  return phraseKeys(keySource)[0] ?? ""
}

function buildDefinitartikelChoices(
  input: SelectCorrectChoiceInput,
): SelectionChoice[] {
  const answerIndex = DEFINITARTIKEL_FORMS.indexOf(
    (input.match.article ?? "").toLowerCase(),
  )
  if (answerIndex === -1) {
    throw new Error(`Unknown definite article "${input.match.article}"`)
  }

  return orderPair({
    answerIndex,
    distractorIndex: pickDistractorIndex({
      answerIndex,
      forms: DEFINITARTIKEL_FORMS,
      pickRandom: input.pickRandom,
    }),
    forms: DEFINITARTIKEL_FORMS,
    isCapitalized: input.isCapitalized,
  })
}

function buildPrepositionChoices(
  input: SelectCorrectChoiceInput,
): SelectionChoice[] {
  const opposite = OPPOSITE_CASE_PREPOSITION[input.match.caseName] ?? ""
  const answerIndex = RICHTUNGSPRAEPOSITION_FORMS.indexOf(
    (input.match.preposition ?? "").toLowerCase(),
  )
  const distractorIndex = RICHTUNGSPRAEPOSITION_FORMS.indexOf(
    opposite.toLowerCase(),
  )
  const hasContrastingPair =
    answerIndex !== -1 &&
    distractorIndex !== -1 &&
    answerIndex !== distractorIndex
  if (!hasContrastingPair) {
    throw new Error(
      `No opposite-case preposition for case "${input.match.caseName}"`,
    )
  }

  return orderPair({
    answerIndex,
    distractorIndex,
    forms: RICHTUNGSPRAEPOSITION_FORMS,
    isCapitalized: input.isCapitalized,
  })
}

function buildLocalPrepositionChoices(
  input: SelectCorrectChoiceInput,
): SelectionChoice[] {
  const member = (input.match.preposition ?? "").toLowerCase()
  const answerIndex = LOKALE_PRAEPOSITION_FORMS.indexOf(member)
  if (answerIndex === -1) {
    throw new Error(`Unknown local preposition "${input.match.preposition}"`)
  }

  return orderPair({
    answerIndex,
    distractorIndex: pickDistractorIndex({
      answerIndex,
      forms: LOKALE_PRAEPOSITION_FORMS,
      pickRandom: input.pickRandom,
    }),
    forms: LOKALE_PRAEPOSITION_FORMS,
    isCapitalized: input.isCapitalized,
  })
}

/**
 * Rule 11 — like the local prepositions, the sense decides, not the case. The
 * member set has exactly two entries, so the one false form is always the other
 * member and the pair keeps the DB's canonical order (`aus · vor`).
 */
function buildKausalePrepositionChoices(
  input: SelectCorrectChoiceInput,
): SelectionChoice[] {
  const member = (input.match.preposition ?? "").toLowerCase()
  const answerIndex = KAUSALE_PRAEPOSITION_FORMS.indexOf(member)
  if (answerIndex === -1) {
    throw new Error(`Unknown causal preposition "${input.match.preposition}"`)
  }

  return orderPair({
    answerIndex,
    distractorIndex: pickDistractorIndex({
      answerIndex,
      forms: KAUSALE_PRAEPOSITION_FORMS,
      pickRandom: input.pickRandom,
    }),
    forms: KAUSALE_PRAEPOSITION_FORMS,
    isCapitalized: input.isCapitalized,
  })
}

function buildParadigmChoices(
  input: SelectCorrectChoiceInput,
): SelectionChoice[] {
  return buildPairChoices(input, endingForms(input))
}

/**
 * Rule 8's contrast sets arrive answer-first, so a fixed order would pin the
 * correct button to one side (alphabetical still left it on the left in 39 of
 * 52 questions). The pair is drawn once per build and its side is flipped by the
 * same injected picker, so it is random across questions but stable for the
 * lifetime of the question.
 */
function buildOrtsadverbChoices(
  input: SelectCorrectChoiceInput,
): SelectionChoice[] {
  const choices = buildPairChoices(input, input.match.options ?? [])
  return input.pickRandom(2) === 0 ? choices : [...choices].reverse()
}

/** One true form plus one false sibling, placed by the order of `forms`. */
function buildPairChoices(
  input: SelectCorrectChoiceInput,
  forms: string[],
): SelectionChoice[] {
  const answerIndex = forms.findIndex(
    form=> normalizeKey(form) === normalizeKey(input.answer),
  )
  if (answerIndex === -1) {
    throw new Error(`Options do not contain the answer "${input.answer}"`)
  }

  return orderPair({
    answerIndex,
    distractorIndex: pickDistractorIndex({
      answerIndex,
      forms,
      pickRandom: input.pickRandom,
    }),
    forms,
    isCapitalized: input.isCapitalized,
  })
}

/**
 * Ending questions offer only the endings this rule answers with. Its DB stores
 * the whole declension paradigm, so the genitive `es` behind `des` would
 * otherwise become a distractor for a rule that never asks for it.
 */
function endingForms(input: SelectCorrectChoiceInput): string[] {
  const forms = input.match.options ?? []
  if (!isEndingMode(input.match)) return forms

  const answerKey = normalizeKey(input.answer)
  const attested = new Set(input.attestedAnswers)
  const kept = forms.filter(form=> {
    const key = normalizeKey(form)
    return key === answerKey || attested.has(key)
  })
  return kept.length > 1 ? kept : forms
}

/** One false form, drawn once per build — never the whole paradigm. */
function pickDistractorIndex(input: {
  answerIndex: number
  forms: string[]
  pickRandom: (length: number)=> number
}): number {
  const candidateIndexes = input.forms
    .map((_, index)=> index)
    .filter(index=> index !== input.answerIndex)
  const picked = candidateIndexes[input.pickRandom(candidateIndexes.length)]
  if (picked === undefined) throw new Error("No false form available")

  return picked
}

/**
 * Two choices placed by the paradigm's own order, so a question never reorders
 * between renders (same behaviour as `select-correct-word`).
 */
function orderPair(input: {
  answerIndex: number
  distractorIndex: number
  forms: string[]
  isCapitalized: boolean
}): SelectionChoice[] {
  return [
    { correct: true, index: input.answerIndex },
    { correct: false, index: input.distractorIndex },
  ]
    .sort((left, right)=> left.index - right.index)
    .map(choice=> ({
      correct: choice.correct,
      value: formatWord({
        isCapitalized: input.isCapitalized,
        word: stripEndingDash(input.forms[choice.index] ?? ""),
      }),
    }))
}

/** Endings ship as `-en` from the idiom DB; the buttons show them bare. */
function stripEndingDash(form: string): string {
  return form.replace(/^-/, "")
}

function formatWord(input: {
  isCapitalized: boolean
  word: string
}): string {
  if (!input.isCapitalized) return input.word
  return input.word.charAt(0).toUpperCase() + input.word.slice(1)
}

function startsWithUppercase(token: string): boolean {
  const firstCharacter = [...token][0] ?? ""
  return (
    firstCharacter !== "" && firstCharacter !== firstCharacter.toLowerCase()
  )
}
